import { z } from 'zod';
import { managedSecretsProviderEnum } from './managed-secrets.js';

export const authTypeSchema = z.enum(['api-key', 'oauth2', 'oauth2-obo', 'internal-jwt', 'none']);
export type AuthType = z.infer<typeof authTypeSchema>;

export const AUTH_TYPE_LABELS: Record<AuthType, string> = {
  'api-key': 'API Key',
  oauth2: 'Client Credentials (OAuth 2.0)',
  'oauth2-obo': 'OAuth 2.0 On-Behalf-Of (OBO)',
  'internal-jwt': 'Internal User Delegation JWT',
  none: 'None',
};

export function getAuthTypeLabel(type: AuthType): string {
  return AUTH_TYPE_LABELS[type];
}

export const authApiKeyDataSchema = z.object({
  secretIdentifier: z.string(),
  header: z.string(),
});
export const authOauth2DataSchema = z.object({
  connectedAppIdentifier: z.string(),
});
export const authOauth2OboDataSchema = z.object({
  connectedAppIdentifier: z.string(),
});
export const authInternalJwtDataSchema = z.object({
  scope: z.string(),
  audience: z.string(),
});

export const authApiKeySchema = z.object({
  type: z.literal('api-key'),
  data: authApiKeyDataSchema,
});
export type AuthApiKey = z.infer<typeof authApiKeySchema>;

export const authOauth2Schema = z.object({
  type: z.literal('oauth2'),
  data: authOauth2DataSchema,
});
export type AuthOauth2 = z.infer<typeof authOauth2Schema>;

export const authOauth2OboSchema = z.object({
  type: z.literal('oauth2-obo'),
  data: authOauth2OboDataSchema,
});
export type AuthOauth2Obo = z.infer<typeof authOauth2OboSchema>;

export const authInternalJwtSchema = z.object({
  type: z.literal('internal-jwt'),
  data: authInternalJwtDataSchema,
});
export type AuthInternalJwt = z.infer<typeof authInternalJwtSchema>;

export const authNoneSchema = z.object({
  type: z.literal('none'),
  data: z.object({}),
});

export const authSchema = z.union([
  authApiKeySchema,
  authOauth2Schema,
  authOauth2OboSchema,
  authInternalJwtSchema,
  authNoneSchema,
]);

export type AuthSchema = z.infer<typeof authSchema>;

///

export const authApiKeyDataDetailsSchema = z.object({
  secret: z.object({
    id: z.number(),
    identifier: z.string(),
    name: z.string(),
    provider: managedSecretsProviderEnum,
    providerKey: z.string(),
  }),
  header: z.string(),
});
export const authOauth2DataDetailsSchema = z.object({
  connectedApp: z.object({
    id: z.number(),
    identifier: z.string(),
    name: z.string(),
  }),
});
export const authInternalJwtDataDetailsSchema = z.object({
  scope: z.string(),
  audience: z.string(),
});

export const authApiKeyDetailsSchema = z.object({
  type: z.literal('api-key'),
  data: authApiKeyDataDetailsSchema,
});
export type AuthApiKeyDetails = z.infer<typeof authApiKeyDetailsSchema>;

export const authNoneDetailsSchema = z.object({
  type: z.literal('none'),
  data: z.object({}),
});

export type AuthNoneDetails = z.infer<typeof authNoneDetailsSchema>;

export const authOauth2DetailsSchema = z.object({
  type: z.literal('oauth2'),
  data: authOauth2DataDetailsSchema,
});
export type AuthOauth2Details = z.infer<typeof authOauth2DetailsSchema>;

export const authOauth2OboDetailsSchema = z.object({
  type: z.literal('oauth2-obo'),
  data: authOauth2DataDetailsSchema,
});
export type AuthOauth2OboDetails = z.infer<typeof authOauth2OboDetailsSchema>;

export const authInternalJwtDetailsSchema = z.object({
  type: z.literal('internal-jwt'),
  data: authInternalJwtDataDetailsSchema,
});
export type AuthInternalJwtDetails = z.infer<typeof authInternalJwtDetailsSchema>;

export const authDetailsSchema = z.union([
  authApiKeyDetailsSchema,
  authOauth2DetailsSchema,
  authOauth2OboDetailsSchema,
  authInternalJwtDetailsSchema,
  authNoneDetailsSchema,
]);

export type AuthDetailsSchema = z.infer<typeof authDetailsSchema>;